/**
 * Auth State Management Store (Zustand)
 * Manages authentication state and user information
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { apiConfig } from '@flow-studio/api';
import type { UIDesignerMode, UserPermissions } from '@flow-studio/types';

export interface User {
  userId: number;
  username: string;
  tenantId: number;
  email?: string;
  displayName?: string;
  roles?: string[];
  permissions?: UserPermissions;
}

interface AuthStoreState {
  user: User | null;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User) => void;
  clearUser: () => void;
  getTenantId: () => number;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  canAccessMode: (mode: UIDesignerMode) => boolean;
}

/**
 * Auth Store
 * Manages user authentication and tenant information
 */
export const useAuthStore = create<AuthStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        user: null,
        isAuthenticated: false,

        // Actions
        setUser: (user: User) =>
          set({
            user,
            isAuthenticated: true,
          }),

        clearUser: () =>
          set({
            user: null,
            isAuthenticated: false,
          }),

        /**
         * Get Tenant ID
         * Returns the tenant ID from the logged-in user or falls back to config
         */
        getTenantId: () => {
          const state = get();
          return state.user?.tenantId || apiConfig.tenantId;
        },

        /**
         * Check if user has a specific permission
         * Defaults to allowing access if no permissions are defined
         */
        hasPermission: (permission: keyof UserPermissions) => {
          const user = get().user;
          if (!user || !user.permissions) {
            // Default allow if no permissions defined
            return true;
          }
          return user.permissions[permission] ?? false;
        },

        /**
         * Check if user can access a specific designer mode
         * Based on user permissions
         */
        canAccessMode: (mode: UIDesignerMode) => {
          const { hasPermission } = get();
          switch (mode) {
            case 'design':
              return hasPermission('canEditWorkflows');
            case 'execution':
              return hasPermission('canExecuteWorkflows');
            case 'evaluation':
              return hasPermission('canViewExecutionHistory');
            default:
              return false;
          }
        },
      }),
      {
        // NOT 'auth-store' — @passport/store's real authStore (introduced app-wide by this
        // app's SSO migration, see apps/flow-studio/src/App.tsx) persists under that exact
        // same localStorage key with a completely different User shape
        // ({id, email, name, roles, permissions} vs. this store's {userId, username,
        // tenantId, ...}). Sharing the key would mean whichever store rehydrates last
        // clobbers/shadows the other's session data across reloads, and getTenantId()/
        // hasPermission() below would silently start reading a shape-mismatched object
        // instead of null — turning today's harmless "no user set" default-allow behavior
        // (see hasPermission's comment) into a false-deny once a real @passport/store
        // session exists. This store's own setUser()/clearUser() have no call sites
        // anywhere in the app (verified before this migration), so it is — and remains —
        // permanently empty; the rename just keeps it from silently reading someone else's
        // localStorage key.
        name: 'flow-studio-legacy-auth-store',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
